import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useRole } from '../context/RoleContext';
import { useT } from '../i18n';
import { api } from '../api/client';
import { ConfirmModal } from '../components/ConfirmModal';
import { DropdownSelect } from '../components/DropdownSelect';
import type { Project, GridColumn, GridRow, ChangeRequest } from '../types';
import { ChangeRequestReview } from '../components/ChangeRequestReview';
import { EMPTY_FORM, parseJSON, countFilled, projectToForm, formToPayload } from '../components/ProjectForm';

// ═══════════════════════════════════════════
// INBOX PAGE
// ═══════════════════════════════════════════

type InboxTab = 'inbox' | 'changes';

const CR_STATUS_I18N_KEY: Record<string, string> = {
  '': 'inbox.statusAll',
  pending: 'inbox.statusPending',
  approved: 'inbox.statusApproved',
  rejected: 'inbox.statusRejected',
};

/** The day an initiative was posted, as dd.mm.yyyy in local time. */
function postedDay(createdAt: string): string {
  const d = new Date(createdAt);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

export function InboxPage() {
  const { isAdmin, isDirection } = useRole();
  const { t } = useT();
  const [projects, setProjects] = useState<Project[]>([]);
  // Domains and levels: the change-request review shows them by name.
  const [portfolioDomains, setPortfolioDomains] = useState<GridColumn[]>([]);
  const [levels, setLevels] = useState<GridRow[]>([]);
  const [pipelineColumns, setPipelineColumns] = useState<GridColumn[]>([]);
  const [loading, setLoading] = useState(true);


  // Tab state
  const [activeTab, setActiveTab] = useState<InboxTab>('inbox');

  // Change requests
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [crFilter, setCrFilter] = useState<string>('');
  const [reviewingCR, setReviewingCR] = useState<ChangeRequest | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; context: 'list' | 'form' } | null>(null);

  const loadData = useCallback(async () => {
    const [allP, pipeCols, domainCols, rows] = await Promise.all([
      api.getProjects() as Promise<Project[]>,
      api.getGridColumns('pipeline') as Promise<GridColumn[]>,
      api.getGridColumns('portfolio') as Promise<GridColumn[]>,
      api.getGridRows() as Promise<GridRow[]>,
    ]);
    setPortfolioDomains(domainCols);
    setLevels(rows);
    setPipelineColumns(pipeCols);
    const inboxCol = pipeCols[0];
    if (inboxCol) setProjects(allP.filter(p => p.status_id === inboxCol.id));
    setLoading(false);
  }, []);

  const loadChangeRequests = useCallback(async () => {
    try {
      const all = await api.getChangeRequests() as ChangeRequest[];
      setChangeRequests(all);
      setPendingCount(all.filter(cr => cr.status === 'pending').length);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  // Opening the inbox counts as having seen what's in it (sidebar badge).
  useEffect(() => {
    api.markInboxSeen().then(() => window.dispatchEvent(new Event('pmo:inbox-seen'))).catch(() => {});
  }, []);
  useEffect(() => { loadChangeRequests(); }, [loadChangeRequests]);

  // Keep change-request status fresh: refetch when the tab regains focus or the
  // user navigates (back) to the inbox — e.g. after clicking an "approved"
  // notification — so a reviewed request never lingers as "pending".
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') {
        loadChangeRequests();
        loadData();
      }
    };
    const onHash = () => {
      if (window.location.hash.startsWith('#inbox')) refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('hashchange', onHash);
    };
  }, [loadChangeRequests, loadData]);

  // Parse tab and review from URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('tab=changes')) {
      setActiveTab('changes');
    }
    const reviewMatch = hash.match(/[?&]review=(\d+)/);
    if (reviewMatch) {
      const crId = parseInt(reviewMatch[1], 10);
      api.getChangeRequest(crId).then((cr) => {
        setReviewingCR(cr as ChangeRequest);
        setActiveTab('changes');
      }).catch(() => {});
    }
  }, []);

  // Old links to the edit form (#inbox?edit=ID, from the lists, Livrables,
  // People…) now open the project page; an old stage-tab name is understood there.
  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/[?&]edit=(\d+)/);
    if (!match) return;
    const tab = hash.match(/[?&]tab=([^&]+)/)?.[1];
    window.location.replace(`#project?id=${match[1]}${tab ? `&tab=${tab}` : ''}`);
  }, []);

  // An initiative opens on its project page (the "À faire · Demande" view).
  const openEdit = (p: Project) => { window.location.hash = `project?id=${p.id}`; };

  // A new initiative: named here, created, then completed on its project page.
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const openCreate = () => { setNewName(''); setCreateError(null); setCreating(true); };
  const createInitiative = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const created = await api.createProject(formToPayload({ ...EMPTY_FORM, name })) as Project;
      setCreating(false);
      window.location.hash = `project?id=${created.id}`;
    } catch (err: any) {
      setCreateError(err?.message || t('inbox.createFailed'));
    }
  };

  // Keep the hash in sync with the list's Inbox/Changes tab and any open CR
  // review — via replaceState, so the address bar is a shareable link.
  useEffect(() => {
    if (loading || /[?&]edit=/.test(window.location.hash)) return;
    const params = new URLSearchParams();
    if (activeTab === 'changes') params.set('tab', 'changes');
    if (activeTab === 'changes' && reviewingCR) params.set('review', String(reviewingCR.id));
    const qs = params.toString();
    const newHash = '#inbox' + (qs ? `?${qs}` : '');
    if (window.location.hash !== newHash) window.history.replaceState(null, '', newHash);
  }, [loading, activeTab, reviewingCR]);

  const handleDelete = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget({ id, context: 'list' });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const { id, context } = deleteTarget;
    setDeleteTarget(null);
    await api.deleteProject(id);
    loadData();
  };

  const handleStatusChange = async (project: Project, statusId: number) => {
    await api.updateProjectStatus(project.id, statusId);
    loadData();
  };

  const handleApproveCR = async (comment: string, forceApply: boolean) => {
    if (!reviewingCR) return;
    await api.approveChangeRequest(reviewingCR.id, comment, forceApply);
    setReviewingCR(null);
    loadChangeRequests();
    loadData();
  };

  const handleRejectCR = async (comment: string) => {
    if (!reviewingCR) return;
    await api.rejectChangeRequest(reviewingCR.id, comment);
    setReviewingCR(null);
    loadChangeRequests();
  };

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;

  // ── Direction user: show their change requests ──
  if (!isAdmin && isDirection) {
    const filteredCRs = crFilter
      ? changeRequests.filter(cr => cr.status === crFilter)
      : changeRequests;

    return (
      <div className="page">
        <div className="flex justify-between items-center mb-20">
          <div>
            <h1 className="page-title">{t('inbox.myChangeRequests')}</h1>
            <p className="page-meta">{t('inbox.pendingCount', { n: changeRequests.filter(cr => cr.status === 'pending').length })}</p>
          </div>
        </div>

        <div className="cr-filter-bar">
          {['', 'pending', 'approved', 'rejected'].map((f) => (
            <button
              key={f}
              className={`btn btn-sm ${crFilter === f ? 'btn-accent' : 'btn-ghost'}`}
              onClick={() => setCrFilter(f)}
            >
              {t(CR_STATUS_I18N_KEY[f])}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-8">
          {filteredCRs.map((cr) => (
            <div key={cr.id} className="cr-card">
              <div className="cr-card-header">
                <span className="cr-card-project">{cr.project_name}</span>
                <span className={`badge badge-cr-${cr.status}`}>{t(CR_STATUS_I18N_KEY[cr.status])}</span>
              </div>
              <div className="cr-card-meta">
                <span>{new Date(cr.created_at).toLocaleDateString()}</span>
                {cr.reviewer_comment && (
                  <span className="cr-card-comment">{t('inbox.quotedComment', { comment: cr.reviewer_comment })}</span>
                )}
              </div>
            </div>
          ))}
          {filteredCRs.length === 0 && (
            <div className="empty-state-card">
              <div className="empty-state">
                <div className="empty-state-title">{t('inbox.noChangeRequestsTitle')}</div>
                <div className="empty-state-desc">
                  {t('inbox.noChangeRequestsDescUser')}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Admin: Tabbed list view ──
  const filteredCRs = crFilter
    ? changeRequests.filter(cr => cr.status === crFilter)
    : changeRequests;

  return (
    <div className="page">
      {/* Tab bar */}
      <div className="inbox-tabs">
        <button
          className={`inbox-tab ${activeTab === 'inbox' ? 'inbox-tab--active' : ''}`}
          onClick={() => setActiveTab('inbox')}
        >
          {t('inbox.tabIncoming')}
        </button>
        <button
          className={`inbox-tab ${activeTab === 'changes' ? 'inbox-tab--active' : ''}`}
          onClick={() => setActiveTab('changes')}
        >
          {t('inbox.tabChanges')}
          {pendingCount > 0 && <span className="tab-badge">{pendingCount}</span>}
        </button>
      </div>

      {activeTab === 'inbox' && (
        <>
          <div className="flex justify-between items-center mb-20">
            <div>
              <p className="page-meta">{t(projects.length === 1 ? 'inbox.awaitingReviewOne' : 'inbox.awaitingReviewMany', { n: projects.length })}</p>
            </div>
            <button className="btn btn-accent" onClick={openCreate}>
              <Plus className="icon-sm" /> {t('inbox.newInitiative')}
            </button>
          </div>

          <div className="flex flex-col gap-8">
            {projects.map((project) => {
              const va = parseJSON<string[]>(project.value_added, []);
              const res = parseJSON<any>(project.resources_effort, {});
              const filled = countFilled(projectToForm(project));

              return (
                <div key={project.id} className="inbox-card" onClick={() => openEdit(project)}>
                  <div className="inbox-card-header">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="inbox-card-name">{project.name}</div>
                      {project.goal && <div className="inbox-card-goal">{project.goal}</div>}
                    </div>
                    <div className="inbox-card-actions" onClick={(e) => e.stopPropagation()}>
                      <DropdownSelect
                        value={project.status_id}
                        options={pipelineColumns.map((col) => ({ value: col.id, label: col.label }))}
                        onChange={(val) => handleStatusChange(project, Number(val))}
                        triggerStyle={{ width: 'auto', fontSize: 'var(--t-11)', padding: '2px 8px' }}
                        dropdownStyle={{ maxHeight: 'none' }}
                      />
                      <button className="btn-icon" title={t('inbox.deleteTitle')} onClick={(e) => handleDelete(project.id, e)}>
                        <Trash2 className="icon-sm" />
                      </button>
                    </div>
                  </div>
                  <div className="inbox-card-meta">
                    {va.map((v) => (
                      <span key={v} className="badge badge-accent">{v}</span>
                    ))}
                    {res.effort_size && <span className="badge">{res.effort_size}</span>}
                    {project.created_at && (
                      <span className="text-sm text-ink-3">{project.created_by_name
                        ? t('inbox.postedOnBy', { date: postedDay(project.created_at), name: project.created_by_name })
                        : t('inbox.postedOn', { date: postedDay(project.created_at) })}</span>
                    )}
                    <div className="completion-bar ml-auto" title={t('inbox.sectionsFilled', { filled })}>
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className={`completion-seg${i < filled ? ' filled' : ''}`} />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}

            {projects.length === 0 && (
              <div className="empty-state-card">
                <div className="empty-state">
                  <div className="empty-state-title">{t('inbox.noInitiativesTitle')}</div>
                  <div className="empty-state-desc">
                    {t('inbox.noInitiativesDesc')}
                  </div>
                  <button className="btn btn-accent mt-16" onClick={openCreate}>
                    <Plus className="icon-sm" /> {t('inbox.newInitiative')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'changes' && (
        <>
          {reviewingCR ? (
            <ChangeRequestReview
              cr={reviewingCR}
              domains={portfolioDomains}
              levels={levels}
              pipelineColumns={pipelineColumns}
              onApprove={handleApproveCR}
              onReject={handleRejectCR}
              onClose={() => setReviewingCR(null)}
            />
          ) : (
            <>
              <div className="cr-filter-bar">
                {['', 'pending', 'approved', 'rejected'].map((f) => (
                  <button
                    key={f}
                    className={`btn btn-sm ${crFilter === f ? 'btn-accent' : 'btn-ghost'}`}
                    onClick={() => setCrFilter(f)}
                  >
                    {t(CR_STATUS_I18N_KEY[f])}
                  </button>
                ))}
              </div>

              <div className="flex flex-col gap-8">
                {filteredCRs.map((cr) => (
                  <div
                    key={cr.id}
                    className={`cr-card ${cr.status === 'pending' ? 'cr-card--pending' : ''}`}
                    onClick={() => setReviewingCR(cr)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="cr-card-header">
                      <span className="cr-card-project">{cr.project_name}</span>
                      <span className={`badge badge-cr-${cr.status}`}>{t(CR_STATUS_I18N_KEY[cr.status])}</span>
                    </div>
                    <div className="cr-card-meta">
                      <span>{t('inbox.byRequester', { name: cr.requester_name })}</span>
                      <span>{new Date(cr.created_at).toLocaleDateString()}</span>
                      {cr.reviewer_comment && (
                        <span className="cr-card-comment">{t('inbox.quotedComment', { comment: cr.reviewer_comment })}</span>
                      )}
                    </div>
                  </div>
                ))}
                {filteredCRs.length === 0 && (
                  <div className="empty-state-card">
                    <div className="empty-state">
                      <div className="empty-state-title">{t('inbox.noChangeRequestsTitle')}</div>
                      <div className="empty-state-desc">
                        {t('inbox.noChangeRequestsDescAdmin')}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {creating && (
        <div className="modal-overlay" onClick={() => setCreating(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, width: '100%' }}>
            <div className="modal-header">
              <h3 className="modal-title">{t('inbox.newInitiative')}</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">{t('inbox.newNameLabel')}</label>
                <input className="form-input" autoFocus value={newName} placeholder={t('inbox.newNamePlaceholder')}
                  onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') createInitiative(); }} />
              </div>
              <p className="pd-note">{t('inbox.newHelp')}</p>
              {createError && <p className="text-err text-sm">{createError}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setCreating(false)}>{t('common.cancel')}</button>
              <button className="btn btn-accent" disabled={!newName.trim()} onClick={createInitiative}>{t('inbox.createAndOpen')}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={deleteTarget !== null}
        title={t('inbox.deleteInitiativeTitle')}
        message={t('inbox.deleteInitiativeMessage')}
        confirmLabel={t('inbox.delete')}
        destructive
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
